import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import Region from '../Region';
import UIAssetKeys from '../UIAssetKeys';
import gameData, { AREAS, getFoundShapesInArea, getTotalFoundShapes, REGIONS } from '../gameData';
import ChairArea from './ChairArea';
import TreesArea from './TreesArea';
import TruckArea from './TruckArea';
import TentArea from './TentArea';
import SoundAssetKeys from '../SoundAssetKeys';

export default class CampRegion extends Region
{
    /**
     * Create the Camp region screen
     * @param {Phaser.Scene} scene the owner scene
     */
    constructor(scene)
    {
        super(scene, REGIONS.CAMP);

        this.isLocked = gameData.region_unlock_requirements[REGIONS.CAMP] > getTotalFoundShapes();

        this.background = ABCya.Images.addImage(0, 0, UIAssetKeys._09_Camping_Cmp_BG, this).setOrigin(0);

        this._addGraphics();
        this._createMenuButton(UIAssetKeys._Buttons_Camp_Btn_Cmp_Menu3, UIAssetKeys._Buttons_Camp_Btn_Cmp_Menu2, UIAssetKeys._Buttons_Camp_Btn_Cmp_Menu1);
        this._createProgressButton(UIAssetKeys._Buttons_Btn_Cmp_Prgrs3, UIAssetKeys._Buttons_Btn_Cmp_Prgrs2, UIAssetKeys._Buttons_Btn_Cmp_Prgrs1);
        this._createInformation(UIAssetKeys._09_Camping_HldngShp_Cmp, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0xffffff, 0x2c2c89, 0x204243);
        this._createBackButton(UIAssetKeys._Buttons_Camp_Btn_Cmp_Menu3, UIAssetKeys._Buttons_Camp_Btn_Cmp_Back2, UIAssetKeys._Buttons_Camp_Btn_Cmp_Back1);

        this.chairArea = new ChairArea(this);
        this.treesArea = new TreesArea(this);
        this.truckArea = new TruckArea(this);
        this.tentArea = new TentArea(this);

        this._createEventListeners();

        this.chairProgress = this._createAreaProgressCounter(751, 477, UIAssetKeys._09_Camping_HldngShp_Cmp, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].CHAIR);
        this.treesProgress = this._createAreaProgressCounter(354, 340, UIAssetKeys._09_Camping_HldngShp_Cmp, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].TREES);
        this.truckProgress = this._createAreaProgressCounter(570, 110, UIAssetKeys._09_Camping_HldngShp_Cmp, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].TRUCK);
        this.tentProgress = this._createAreaProgressCounter(472, 537, UIAssetKeys._09_Camping_HldngShp_Cmp, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].TENT);

        this._createGreatWorkPopup();
        this._createRegionUnlockedPopup();

        this.jingleAudio = ABCya.Audio.createSound(this.scene);

        //this._addReference("CampRegionZoomedOut", "assets/reference_images/10 Get In Shape_Camping_1.jpg");
        //this._addReference("CampRegionZoomedOut-AreaCounters", "assets/reference_images/10 Get In Shape_Camping_2-01.jpg");
        //this._addReference("CampRegionZoomedIn-Chair", "assets/reference_images/10 Get In Shape_Camping_3-01.jpg");
        //this._addReference("CampRegionZoomedIn-Trees", "assets/reference_images/10 Get In Shape_Camping_5-01.jpg");
        //this._addReference("CampRegionZoomedIn-Truck", "assets/reference_images/10 Get In Shape_Camping_6-01.jpg");
        //this._addReference("CampRegionZoomedIn-Tent", "assets/reference_images/10 Get In Shape_Camping_4-01.jpg");
        //ABCya.Debug.enableImagePlacerDebug(this.scene, this.background, this.ground);

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.chair.disableInteractive();
        this.treeAreaButton.disableInteractive();
        this.tent.disableInteractive();
        this.truck.disableInteractive();

        this.chairArea.reset();
        this.treesArea.reset();
        this.truckArea.reset();
        this.tentArea.reset();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.chair.disableInteractive();
        this.treeAreaButton.disableInteractive();
        this.tent.disableInteractive();
        this.truck.disableInteractive();

        this.chairProgress.setAlpha(0);
        this.truckProgress.setAlpha(0);
        this.tentProgress.setAlpha(0);
        this.treesProgress.setAlpha(0);

        this.updateRegionData();

        this.chairArea.reset();
        this.treesArea.reset();
        this.truckArea.reset();
        this.tentArea.reset();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._campground_drop_in, this.scene, 1, false, 0.5);

        let tweens = [
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.8], props: { y: this.ground.onScreenPosition.y }, offset: 0 },

            { targets: this.shrub, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.shrub.onScreenPosition.y }, offset: 300 },
            { targets: this.sphereShrub, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.sphereShrub.onScreenPosition.y }, offset: 350 },
            { targets: this.smallTree, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.smallTree.onScreenPosition.y }, offset: 400 },
            { targets: this.coneTree, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.coneTree.onScreenPosition.y }, offset: 450 },
            { targets: this.largeTree, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.largeTree.onScreenPosition.y }, offset: 500 },
            { targets: this.circleTree, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.circleTree.onScreenPosition.y }, offset: 550 },
            { targets: this.fireStonesBack, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.fireStonesBack.onScreenPosition.y }, offset: 600 },
            { targets: this.fireStonesFront, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.fireStonesFront.onScreenPosition.y }, offset: 600 },
            { targets: this.fireWood, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.fireWood.onScreenPosition.y }, offset: 650 },
            { targets: this.tent, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.tent.onScreenPosition.y }, offset: 700 },
            { targets: this.chair, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.chair.onScreenPosition.y }, offset: 750 },
            { targets: this.truck, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.truck.onScreenPosition.y }, offset: 800 },
            { targets: this.skyObjects[0], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1200 },
            { targets: this.skyObjects[1], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1250 },
            { targets: this.skyObjects[2], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1300 },
            { targets: this.skyObjects[3], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1350 },
            { targets: this.skyObjects[4], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1400 },
            { targets: this.skyObjects[5], duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 1450 },
            {
                targets: { value: 0 }, duration: 750, ease: Phaser.Math.Easing.Linear, props: { value: 1 }, offset: 1450,
                onUpdate: tween =>
                {
                    this.tentBackLine.setTo(0, 0, 60 * tween.getValue(), 62 * tween.getValue());
                    this.tentFrontLine.setTo(0, 0, -54 * tween.getValue(), 126 * tween.getValue());
                    this.tentFrontLineShadow.setTo(0, 0, 40 * tween.getValue(), 80 * tween.getValue());
                }
            },

            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 1200 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { x: this.informationContainer.onScreenPosition.x }, offset: 1200 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1300 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1400 },
        ];

        this._createAreaProgressFadeIn(tweens, 1500);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.progressButton.setInteractive();

                this.chair.setInteractive();
                this.treeAreaButton.setInteractive();
                this.tent.setInteractive();
                this.truck.setInteractive();

                this._createIdleAnimations();

                ABCya.Audio.playSound(SoundAssetKeys._campground_jingle, this.jingleAudio, 1, true);
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave()
    {
        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.chair.disableInteractive();
        this.treeAreaButton.disableInteractive();
        this.tent.disableInteractive();
        this.truck.disableInteractive();

        this._stopIdleAnimations();

        ABCya.Audio.playOneShot(SoundAssetKeys._campground_jump_up, this.scene, 1, false, 0.75);

        let tweens = [
            { targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 0 }, offset: 0, onComplete: () => this.jingleAudio.stop() },
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 1500 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.8], props: { y: this.ground.offScreenPosition.y }, offset: 1500 },

            // place region specific things here

            { targets: this.shrub, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.shrub.offScreenPosition.y }, offset: 1200 },
            { targets: this.sphereShrub, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.sphereShrub.offScreenPosition.y }, offset: 1150 },
            { targets: this.smallTree, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.smallTree.offScreenPosition.y }, offset: 1100 },
            { targets: this.coneTree, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.coneTree.offScreenPosition.y }, offset: 1050 },
            { targets: this.largeTree, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.largeTree.offScreenPosition.y }, offset: 1000 },
            { targets: this.circleTree, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.circleTree.offScreenPosition.y }, offset: 950 },
            { targets: this.fireStonesBack, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.fireStonesBack.offScreenPosition.y }, offset: 900 },
            { targets: this.fireStonesFront, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.fireStonesFront.offScreenPosition.y }, offset: 900 },
            { targets: this.fireWood, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.fireWood.offScreenPosition.y }, offset: 850 },
            { targets: this.tent, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.tent.offScreenPosition.y }, offset: 800 },
            { targets: this.chair, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.chair.offScreenPosition.y }, offset: 750 },
            { targets: this.truck, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.truck.offScreenPosition.y }, offset: 700 },
            { targets: this.skyObjects[0], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 300 },
            { targets: this.skyObjects[1], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 250 },
            { targets: this.skyObjects[2], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 200 },
            { targets: this.skyObjects[3], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 150 },
            { targets: this.skyObjects[4], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 100 },
            { targets: this.skyObjects[5], duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 50 },
            {
                targets: { value: 1 }, duration: 750, ease: Phaser.Math.Easing.Linear, props: { value: 0 }, offset: 50,
                onUpdate: tween =>
                {
                    this.tentBackLine.setTo(0, 0, 60 * tween.getValue(), 62 * tween.getValue());
                    this.tentFrontLine.setTo(0, 0, -54 * tween.getValue(), 126 * tween.getValue());
                    this.tentFrontLineShadow.setTo(0, 0, 40 * tween.getValue(), 80 * tween.getValue());
                }
            },

            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 300 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { x: this.informationContainer.offScreenPosition.x }, offset: 300 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 200 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 100 },
            {
                targets: [this.truckProgress, this.chairProgress, this.treesProgress, this.tentProgress],
                duration: 500,
                ease: Phaser.Math.Easing.Quadratic.In,
                props: { alpha: 0 },
                offset: 0
            }
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        super._sendTargetsOffScreen();

        this._sendTargetsOffScreenAll(
            [this.ground, this.circleTree, this.coneTree, this.sphereShrub, this.tent, this.shrub, this.smallTree, this.largeTree, this.chair,
            this.fireStonesBack, this.fireWood, this.fireStonesFront, this.truck]);

        this.shadows.forEach(shadow => shadow.setAlpha(0));
        this.background.setAlpha(0);
        this.skyObjects.forEach(obj => obj.setAlpha(0));

        this.tentFrontLine.setTo(0, 0, 0, 0);
        this.tentBackLine.setTo(0, 0, 0, 0);
        this.tentFrontLineShadow.setTo(0, 0, 0, 0);
    }

    /**
     * Adds the graphics for the region.
     */
    _addGraphics()
    {
        // left side stars
        this.star0 = ABCya.Images.addImage(166, 270, UIAssetKeys._09_Camping_Star, this).setScale(0.8);
        this.star1 = ABCya.Images.addImage(430, 116, UIAssetKeys._09_Camping_Star, this);
        this.star2 = ABCya.Images.addImage(483, 57, UIAssetKeys._09_Camping_Star, this);

        // right side stars
        this.star3 = ABCya.Images.addImage(778, 49, UIAssetKeys._09_Camping_Star, this).setScale(-1, 1);
        this.star4 = ABCya.Images.addImage(813, 159, UIAssetKeys._09_Camping_Star, this).setScale(-0.8, 0.8);

        this.moon = ABCya.Images.addImage(663, 88, UIAssetKeys._09_Camping_Moon, this);

        this.ground = ABCya.Images.addImage(565, 442, UIAssetKeys._09_Camping_Cmp_Grnd, this);

        this.tentShadow = ABCya.Images.addImage(544, 488, UIAssetKeys._09_Camping_Tnt_Shdw, this).setOrigin(0.78654, 0.90278);
        this.chairShadow = ABCya.Images.addImage(650, 529, UIAssetKeys._09_Camping_Chair_Shdw_2, this).setOrigin(0.075, 0.95484);

        this.truck = ABCya.Images.addImage(526, 226, UIAssetKeys._10_Camping_Zoom_In_Truck_Trk_1, this).setScale(0.4393).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.add(this.treeAreaContainer = this.scene.add.container());
        this.circleTree = ABCya.Images.addImage(306, 251, UIAssetKeys._10_Camping_Zoom_In_Trees_Tree1, this.treeAreaContainer).setScale(0.5429);
        this.coneTree = ABCya.Images.addImage(235, 319, UIAssetKeys._10_Camping_Zoom_In_Trees_Tree2, this.treeAreaContainer).setScale(0.5429);
        this.sphereShrub = ABCya.Images.addImage(316, 409, UIAssetKeys._10_Camping_Zoom_In_Trees_Shrb_1, this.treeAreaContainer).setScale(0.5429);
        this.treeAreaContainer.add(this.treeAreaButton = this.scene.add.zone(290, 268, 240, 350).setInteractive({ useHandCursor: true }));
        // to debug where the tree area button area is
        //this.scene.add.rectangle(this.treeAreaButton.x, this.treeAreaButton.y, this.treeAreaButton.width, this.treeAreaButton.height, 0xff0000, 0.5);

        this.add(this.tentBackLine = this.scene.add.line(539, 337, 0, 0, 60, 62, 0x77123b, 1).setOrigin(0));
        this.tent = ABCya.Images.addImage(484, 430, UIAssetKeys._10_Camping_Zoom_In_Tent_Tent, this).setScale(0.4109).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });
        this.add(this.tentFrontLine = this.scene.add.line(430, 399, 0, 0, -54, 126, 0xe57b07, 1).setOrigin(0));
        this.add(this.tentFrontLineShadow = this.scene.add.line(336, 445, 0, 0, 40, 80, 0x143332, 0.5).setOrigin(0));

        // non-area trees/shrugs (on the right side)
        this.shrub = ABCya.Images.addImage(715, 302, UIAssetKeys._09_Camping_Shrb, this);
        this.smallTree = ABCya.Images.addImage(777, 277, UIAssetKeys._09_Camping_Evrgrn, this).setScale(0.75);
        this.largeTree = ABCya.Images.addImage(868, 284, UIAssetKeys._09_Camping_Evrgrn, this);

        this.chair = ABCya.Images.addImage(673, 482, UIAssetKeys._10_Camping_Zoom_In_Chair_Chair_2, this).setScale(0.2027).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.fireShadow = ABCya.Images.addImage(565, 578, UIAssetKeys._09_Camping_Fire_Fire_Shdw, this);
        this.fireStonesBack = ABCya.Images.addImage(563, 554, UIAssetKeys._09_Camping_Fire_Fire_Stns_Bk, this);
        this.fireWood = ABCya.Images.addImage(566, 578, UIAssetKeys._09_Camping_Fire_Fire_Wd, this);
        this.fireStonesFront = ABCya.Images.addImage(566, 588, UIAssetKeys._09_Camping_Fire_Fire_Stns_Frnt, this);
        this.fire = ABCya.Images.addImage(566, 581, UIAssetKeys._09_Camping_Fire_Fire_Flm, this).setOrigin(0.5, 1);
        this.fireGlow = ABCya.Images.addImage(564, 544, UIAssetKeys._09_Camping_Fire_Fire_Glow, this);

        this._setAnimationPositionsAll([
            this.ground, this.truck, this.circleTree, this.coneTree, this.sphereShrub, this.tent, this.shrub, this.smallTree, this.largeTree,
            this.chair, this.fireStonesBack, this.fireStonesFront, this.fireWood
        ]);

        this.shadows = [this.tentShadow, this.chairShadow, this.fire, this.fireGlow, this.fireShadow, this.tentFrontLineShadow];

        this.skyObjects = [this.star0, this.star1, this.star2, this.star3, this.star4, this.moon];
    }

    /**
     * Create the event listeners for the objects that allow the user to zoom into the individual areas.
     */
    _createEventListeners()
    {
        this.chair.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(CampRegionStates.CHAIR));
        this.treeAreaButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(CampRegionStates.TREES));
        this.tent.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(CampRegionStates.TENT));
        this.truck.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(CampRegionStates.TRUCK));
    }

    /**
     * Creates the idle animations. If the idle animations are already playing, it stops it, resets everything, and starts it over.
     */
    _createIdleAnimations()
    {
        if (this.idleAnimations != null && this.idleAnimations.isPlaying())
        {
            this._stopIdleAnimations();
        }

        let tweens = [
            { targets: this.fire, duration: 500, ease: Phaser.Math.Easing.Linear, props: { scale: 1.1 }, offset: 0 },
            { targets: this.fireGlow, duration: 500, ease: Phaser.Math.Easing.Linear, props: { scale: 1.05 }, offset: 0 },
            { targets: this.fireShadow, duration: 500, ease: Phaser.Math.Easing.Linear, props: { scale: 0.98 }, offset: 0 },
            { targets: this.chairShadow, duration: 500, ease: Phaser.Math.Easing.Linear, props: { scale: 0.98, alpha: 0.9 }, offset: 0 },
            { targets: this.tentShadow, duration: 500, ease: Phaser.Math.Easing.Linear, props: { scale: 0.98, alpha: 0.9 }, offset: 0 },
            { targets: this.tentFrontLineShadow, duration: 500, ease: Phaser.Math.Easing.Linear, props: { alpha: 0.75 }, offset: 0 },
            {
                targets: { value: 0 }, duration: 500, ease: Phaser.Math.Easing.Linear, props: { value: 1 }, offset: 0,
                onUpdate: tween =>
                {
                    this.tentFrontLineShadow.setTo(2 * tween.getValue(), 1 * tween.getValue(), 40, 80);
                }
            },

            { targets: this.star0, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { scale: 0.75, alpha: 0.9 }, offset: 0 },
            { targets: this.star1, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { scale: 0.95, alpha: 0.85 }, offset: 150 },
            { targets: this.star2, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { scale: 0.95, alpha: 0.8 }, offset: 300 },
            { targets: this.star3, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { scaleX: -0.96, scaleY: 0.96, alpha: 0.75 }, offset: 450 },
            { targets: this.star4, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { scaleX: -0.75, scaleY: 0.75, alpha: 0.7 }, offset: 600 },
            { targets: this.moon, duration: 1000, ease: Phaser.Math.Easing.Linear, props: { alpha: 0.9 }, offset: 750 },
        ];

        this.idleAnimations = this.scene.tweens.timeline({
            tweens: tweens,
            repeat: -1,
            yoyo: true,
        });
    }

    /**
     * Stops the idle animations and resets everything back to their default spots.
     */
    _stopIdleAnimations()
    {
        if (this.idleAnimations == null) return;

        this.idleAnimations.data.forEach((tween) => 
        {
            if (tween.hasStarted)
            {
                tween.data.forEach((data) =>
                {
                    data.target[data.key] = data.start;
                });
            }
        });
        this.idleAnimations.stop();
    }

    /**
     * Call this function to update all the game data associated with this region;
     * This can be used to make the game in sync with the actual game data (after a new game reset for example).
     * Should also be called before entering the region state.
     */
    updateRegionData()
    {
        super.updateRegionData();

        this._updateAreaProgress(this.chairProgress, getFoundShapesInArea(this.region, AREAS[this.region].CHAIR));

        this._updateAreaProgress(this.treesProgress, getFoundShapesInArea(this.region, AREAS[this.region].TREES));

        this._updateAreaProgress(this.tentProgress, getFoundShapesInArea(this.region, AREAS[this.region].TENT));

        this._updateAreaProgress(this.truckProgress, getFoundShapesInArea(this.region, AREAS[this.region].TRUCK));
    }

    /**
     * Zooms into the desired area.
     * @param {string} area the AREAS enum value
     */
    _zoomIntoArea(area)
    {
        super._zoomIntoArea(area);

        this._currentState = area;
        this._stopIdleAnimations();

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.chair.disableInteractive();
        this.treeAreaButton.disableInteractive();
        this.tent.disableInteractive();
        this.truck.disableInteractive();

        let tweens = [];

        if (this._currentState == CampRegionStates.CHAIR)
        {
            tweens.push(...this.chairArea.zoomIn());
        }
        else if (this._currentState == CampRegionStates.TENT)
        {
            tweens.push(...this.tentArea.zoomIn());
        }
        else if (this._currentState == CampRegionStates.TRUCK)
        {
            tweens.push(...this.truckArea.zoomIn());
        }
        else if (this._currentState == CampRegionStates.TREES)
        {
            tweens.push(...this.treesArea.zoomIn());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.truckProgress, this.chairProgress, this.treesProgress, this.tentProgress, this.tentFrontLine, this.tentBackLine,
                    this.shrub, this.smallTree, this.largeTree, this.fire, this.fireGlow, this.fireStonesBack, this.fireStonesFront, this.fireWood,
                    ...this.shadows, ...this.skyObjects
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 0
            },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.backButton, duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
        );

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.backButton.setInteractive();
                if (this._currentState == CampRegionStates.CHAIR)
                {
                    this.chairArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == CampRegionStates.TENT)
                {
                    this.tentArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == CampRegionStates.TRUCK)
                {
                    this.truckArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == CampRegionStates.TREES)
                {
                    this.treesArea.setAreaShapeButtonInteractable(true);
                }
            }
        });
    }

    /**
     * Leaves the zoomed in area.
     */
    _leaveZoomedInState()
    {
        super._leaveZoomedInState();

        this.updateRegionData();

        this.backButton.disableInteractive();

        let tweens = [];

        if (this._currentState == CampRegionStates.CHAIR)
        {
            tweens.push(...this.chairArea.zoomOut());
        }
        else if (this._currentState == CampRegionStates.TENT)
        {
            tweens.push(...this.tentArea.zoomOut());
        }
        else if (this._currentState == CampRegionStates.TRUCK)
        {
            tweens.push(...this.truckArea.zoomOut());
        }
        else if (this._currentState == CampRegionStates.TREES)
        {
            tweens.push(...this.treesArea.zoomOut());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.tentFrontLine, this.tentBackLine, this.shrub, this.smallTree, this.largeTree, this.fire,
                    this.fireGlow, this.fireStonesBack, this.fireStonesFront, this.fireWood,
                ],
                duration: 500,
                props: { alpha: 1 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 500
            },
            { targets: this.backButton, duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 750 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
            { targets: this.shadows, duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
            { targets: this.skyObjects, duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
        );

        this._createAreaProgressFadeIn(tweens, 1000);

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.chairArea.reset();
                this.tentArea.reset();
                this.treesArea.reset();
                this.truckArea.reset();

                this.menuButton.setInteractive();
                this._checkSpaceRegionLockStatus();
                this.progressButton.setInteractive();

                this.chair.setInteractive();
                this.treeAreaButton.setInteractive();
                this.tent.setInteractive();
                this.truck.setInteractive();

                this._createIdleAnimations();
            }
        });
    }

    /**
     * Create area progress fade in tweens for areas that have been found.
     * @param {any[]} tweens the current array of tweens
     * @param {number} offset the offset of the progress fade in tweens
     */
    _createAreaProgressFadeIn(tweens, offset)
    {
        if (gameData.saved_data[this.region][AREAS[this.region].CHAIR].found)
        {
            tweens.push({ targets: this.chairProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[this.region].TRUCK].found)
        {
            tweens.push({ targets: this.truckProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[this.region].TENT].found)
        {
            tweens.push({ targets: this.tentProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[this.region].TREES].found)
        {
            tweens.push({ targets: this.treesProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
    }
}

/**
 * The individual area states for the Camp region.
 */
export const CampRegionStates = {
    CHAIR: "Chair",
    TREES: "Trees",
    TENT: "Tent",
    TRUCK: "Truck"
}